import moment from 'moment';
import { nanoid } from 'nanoid';

/**
 * ТЗ 0081 — SMS студентам через SMSAPI: словари бэкенда и чистые помощники.
 *
 * Статусы, причины и коды приходят строками (бэкенд намеренно не делает из
 * них graphene-enum): сравнивать только с константами отсюда. Каждый
 * словарь — это и список ключей `sms.*`, который проверяет локаль-гейт
 * (`locales/__tests__/sms.locale.test.ts`): код без перевода уронит тест, а
 * не покажет сырой ключ. Незнакомый код экран показывает как есть.
 *
 * Модуль без React, Apollo и i18n — всё здесь проверяется юнит-тестами.
 */

/** Темы SMS (`SmsMessageType.topic`, `messageTemplates(topic:)`). */
export const SMS_TOPICS = [
  'student_sms',
  'group_sms',
  'invoice_payment',
  'lms_access',
  'esign_signature',
  'teacher_sms',
  // ТЗ 0084: SMS лиду Kommo и кандидату дофинансирования — ручные, из их
  // карточек.
  'lead_sms',
  'dofin_candidate_sms',
  // ТЗ 0084, этап 2: лесенки лида — «Напоминания» и «Анкета» (повод —
  // запуск лесенки; получатель в журнале — лид через запуск).
  'lead_reminder',
  'lead_dofin',
] as const;
export type SmsTopic = typeof SMS_TOPICS[number];

/** Тема договоров (0077/0078): её шаблоны читают только роли договоров. */
export const SMS_ESIGN_TOPIC: SmsTopic = 'esign_signature';

/** Ручная SMS преподавателю из его карточки (2026-09-22). */
export const SMS_TEACHER_TOPIC: SmsTopic = 'teacher_sms';

/** ТЗ 0084: ручная SMS лиду Kommo из карточки лида. */
export const SMS_LEAD_TOPIC: SmsTopic = 'lead_sms';

/** ТЗ 0084: ручная SMS кандидату дофинансирования из его карточки. */
export const SMS_CANDIDATE_TOPIC: SmsTopic = 'dofin_candidate_sms';

/** Статус строки очереди. */
export const SMS_STATUSES = [
  'pending',
  'sending',
  'sent',
  'skipped',
  'failed',
] as const;
export type SmsStatus = typeof SMS_STATUSES[number];

/**
 * Причины пропуска (`skipReason`) — `outreach/constants.py` и причины, с
 * которыми пропускается получатель рассылки. Та же семья — причина, по
 * которой SMS студенту сейчас недоступна (`studentSms.reason`) и почему
 * кандидат рассылки исключён (`smsCampaignCandidates.reason`).
 */
export const SMS_SKIP_REASONS = [
  'disabled_on_server',
  'disabled',
  'entity_not_allowed',
  'no_phone',
  'invalid_phone',
  'not_allowlisted',
  'foreign_number',
  'opted_out',
  'closed',
  'stale',
  'superseded',
  'condition_changed',
  'stopped_manually',
  'cancelled',
  'cancelled_by_stop',
  'busy',
  'required',
  'too_long',
  'links_not_allowed',
  'daily_cap',
  'refresh_failed',
  'no_link',
  'no_invoice',
  'unknown_placeholder',
  // ТЗ 0084: текст шага — только шаблон ERP; нет включённого шаблона с кодом
  // шага — SMS не уходит. Сократитель не настроен — длинной ссылкой SMS
  // тоже не уходит.
  'no_template',
  'shortlink_disabled',
] as const;

/**
 * Статусы доставки SMSAPI (`dlrStatus`, колбэк). `none` — фильтр журнала
 * «отчёта о доставке ещё нет».
 */
export const SMS_DLR_STATUSES = [
  'DELIVERED',
  'UNDELIVERED',
  'FAILED',
  'REJECTED',
  'EXPIRED',
  'NOT_FOUND',
  'SENT',
  'QUEUE',
  'ACCEPTED',
  'UNKNOWN',
  'RENEWAL',
  'STOP',
] as const;
export const SMS_DLR_FILTERS = [...SMS_DLR_STATUSES, 'none'] as const;

/** Финальный отказ оператора — красным, а не «в пути». */
const DLR_BAD = ['UNDELIVERED', 'FAILED', 'REJECTED', 'EXPIRED', 'NOT_FOUND'];

/**
 * Коды отказов мутаций и причин предпросмотра (`customErrors.code`,
 * `SmsPreviewType.reason`). Перевод — `sms.errors.<CODE>`; незнакомый код —
 * текст сервера (`message`), а не пустота.
 */
export const STUDENT_SMS_ERROR_CODES = [
  'NO_PHONE',
  'OPTED_OUT',
  'SMS_DISABLED',
  'NOT_ALLOWLISTED',
  'FOREIGN_NUMBER',
  'TOO_LONG',
  'NO_LINK',
  'NO_INVOICE',
  'INVALID_URL',
  'INVALID_LINK',
  'NO_SIGN_DOCUMENT',
  'NOT_IN_GROUP',
  'NO_RECIPIENTS',
  'INVALID_MODE',
  'INVALID_PHONE',
  'NO_LMS_URL',
  'SHORTLINK_DISABLED',
  'UNKNOWN_PLACEHOLDER',
  'UNKNOWN_TEMPLATE',
  'NOT_RETRYABLE',
  'NOT_CANCELLABLE',
  'NOT_FOUND',
  'CANCELLED',
  'CLOSED',
  'BUSY',
  // Отказы карточки договора (ТЗ 0077/0078) — те же слова, что у остальных
  // SMS: карточка договора говорит на общем языке раздела.
  'WRONG_STATUS',
  'STOPPED',
  'NOTHING_TO_STOP',
  'NOT_STOPPED',
  'DUPLICATE',
  // ТЗ 0082: номера шагов разошлись с порядком состояний; шаг не сойдётся
  // никогда («после открытия, если не открыл»).
  'CONDITION_ORDER',
  'IMPOSSIBLE_CONDITION',
  'FORBIDDEN',
  'REQUIRED',
  'DAILY_CAP',
  'COOLDOWN',
  'INVALID',
  'NO_ACCESS',
  'NETWORK',
  // ТЗ 0083: SMS подписанту — переписка договора, её ведут продавец,
  // аккаунт-менеджеры и Admin.
  'NOT_YOUR_CONTRACT',
  // ТЗ 0084: лид пропал из зеркала; адрес анкеты дофинансирования не
  // настроен на сервере (ссылка «на анкету»).
  'LEAD_NOT_FOUND',
  'NO_FORM_URL',
  // ТЗ 0084: тексты — только шаблоны ERP. Нет шаблона («Напомнить сейчас»),
  // шаблон занят лесенкой (выключить нельзя), `{link}` в теме без ссылки,
  // хвост отписки без `{link}`.
  'NO_TEMPLATE',
  'TEMPLATE_IN_USE',
  'NO_LINK_HERE',
  'LINK_REQUIRED',
  // Перепроверка 0084: в хвосте отписки нужна именно `{link}/stop` — просто
  // `{link}` ведёт на страницу ссылки, а не на отписку.
  'STOP_LINK_REQUIRED',
  // Договор уже подписан: SMS подписанту можно, но без `{link}` — ссылки на
  // подпись больше нет.
  'LINK_NOT_READY',
] as const;

/**
 * Вид ссылки в SMS — все, какие знает составитель: `SmsLinkInput.kind`
 * (студенту и группе), `TeacherSmsLinkInput.kind` (преподавателю, `sign`) и
 * `LeadSmsLinkInput.kind` (лиду — анкета дофинансирования, `dofin_form`).
 * Какие предложить — решает экран своим списком ниже.
 */
export const SMS_LINK_KINDS = [
  'none',
  'invoice',
  'lms',
  'lms_invoices',
  'sign',
  'url',
  'dofin_form',
] as const;
export type SmsLinkKind = typeof SMS_LINK_KINDS[number];

/** Ссылки SMS студенту и рассылки по группе (порядок — порядок выпадашки). */
export const STUDENT_SMS_LINK_KINDS: readonly SmsLinkKind[] = [
  'none',
  'invoice',
  'lms',
  'lms_invoices',
  'url',
];

/**
 * Ссылки SMS преподавателю (решение владельца 2026-09-22): на подпись его
 * документа, LMS, любой адрес — или без ссылки.
 */
export const TEACHER_SMS_LINK_KINDS: readonly SmsLinkKind[] = [
  'none',
  'sign',
  'lms',
  'url',
];

/**
 * ТЗ 0084: ссылки SMS лиду — любой адрес или анкета дофинансирования (сервер
 * сам соберёт `DOFIN_FORM_URL` с меткой лида), или без ссылки.
 */
export const LEAD_SMS_LINK_KINDS: readonly SmsLinkKind[] = [
  'none',
  'url',
  'dofin_form',
];

/**
 * ТЗ 0084: ссылки SMS кандидату дофинансирования — он анкету уже заполнил,
 * поэтому только любой адрес или без ссылки.
 */
export const CANDIDATE_SMS_LINK_KINDS: readonly SmsLinkKind[] = ['none', 'url'];

/** Вид документа на подпись преподавателя (`TeacherSignDocumentType.kind`). */
export const TEACHER_SIGN_DOCUMENT_KINDS = [
  'teacher_framework',
  'teacher_annex',
] as const;

/** Кому рассылка по группе (`sendGroupSms(mode:)`). */
export const SMS_CAMPAIGN_MODES = ['all', 'unpaid', 'overdue'] as const;
export type SmsCampaignMode = typeof SMS_CAMPAIGN_MODES[number];

/** Кодировки SMS: `gsm7` — 160 знаков в части, `ucs2` — 70. */
export const STUDENT_SMS_ENCODINGS = ['gsm7', 'ucs2'] as const;

/** Статус имени отправителя в SMSAPI (`senderStatus`). */
export const SMS_SENDER_STATUSES = [
  'ACTIVE',
  'PENDING',
  'INACTIVE',
  'REJECTED',
  'DELETED',
  'NOT_FOUND',
  'CONSOLE',
] as const;

/** Провайдер канала (`SmsSettingsType.provider`). */
export const SMS_PROVIDERS = ['console', 'smsapi'] as const;

/** Тип счёта SMSAPI (`paymentType`). */
export const SMS_PAYMENT_TYPES = ['prepaid', 'postpaid'] as const;

/**
 * Лесенки, которые правятся в ERP (`reminderPlans`). ТЗ 0084: лесенки лида
 * засеяны ВЫКЛЮЧЕННЫМИ — включает Admin в «SMS → Лесенки».
 */
export const SMS_PLAN_TOPICS = [
  'esign_signature',
  'invoice_payment',
  'lead_reminder',
  'lead_dofin',
] as const;

/**
 * Якоря шага лесенки. Какие из них доступны теме — говорит сервер
 * (`ReminderPlanType.anchors`); этот список нужен только для перевода
 * подписей и запасного варианта.
 */
export const SMS_PLAN_ANCHORS = [
  'start',
  'clicked',
  'opened',
  'deadline',
] as const;
export type SmsPlanAnchor = typeof SMS_PLAN_ANCHORS[number];

/**
 * ТЗ 0082: состояния поводов, у которых есть перевод. Словарь темы
 * приходит с сервера (`ReminderPlanType.states`) — он и решает, что
 * показать; незнакомое состояние покажется ключом, а не пропадёт.
 */
export const SMS_PLAN_CONDITIONS = [
  'not_opened',
  'clicked',
  'opened',
  'unpaid',
  'partly_paid',
  'overdue',
] as const;

/** Единицы ввода сдвига шага. Хранится всё в минутах. */
export const SMS_OFFSET_UNITS = ['minutes', 'hours', 'days'] as const;
export type SmsOffsetUnit = typeof SMS_OFFSET_UNITS[number];
export const SMS_OFFSET_UNIT_MINUTES: Record<SmsOffsetUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 24 * 60,
};

/** Вкладки страницы «SMS» — в адресе `?tab=`; первая по умолчанию. */
export const SMS_PAGE_TABS = [
  'journal',
  'settings',
  'plans',
  'templates',
] as const;
export type SmsPageTab = typeof SMS_PAGE_TABS[number];

/**
 * Темы вкладки «Шаблоны» (`TEMPLATE_TOPICS` бэкенда) — все темы SMS. У
 * лесенок (подпись, оплата, лиды) шаблон с кодом шага — это и есть текст,
 * который шлёт робот (перепроверка 0084: тексты лесенок лида — системные
 * шаблоны `lead_*_first` / `*_nudge`).
 */
export const SMS_TEMPLATE_TOPICS: readonly SmsTopic[] = SMS_TOPICS;

/**
 * Темы, у которых есть шаблоны ПИСЕМ (`EMAIL_TEMPLATE_TOPICS` бэкенда):
 * договоры и — ТЗ 0084 — письма лиду («Приглашение к анкете», «Первое
 * письмо»).
 */
export const SMS_EMAIL_TEMPLATE_TOPICS: readonly SmsTopic[] = [
  'esign_signature',
  'lead_sms',
];

/**
 * Подстановки, у которых есть свой перевод подсказки (`sms.variables.<name>`).
 * Список с сервера (`messageTemplateVariables(topic)`) главнее: он решает,
 * какие чипы показать; этот — только для перевода описаний (описание с
 * сервера написано по-русски и в польском интерфейсе читалось бы чужим).
 */
export const SMS_VARIABLE_NAMES = [
  'first_name',
  'last_name',
  'group',
  'course',
  'link',
  'number',
  'amount',
  'currency',
  'due_date',
  'days_overdue',
  'invoices_count',
  'expires',
  'seller_name',
  'start_date',
] as const;

/**
 * Имя и фамилия у темы преподавателя — его, а не студента: подсказка
 * «Имя студента» над SMS преподавателю врала бы.
 */
const SMS_TEACHER_VARIABLE_HINTS: Readonly<Record<string, string>> = {
  first_name: 'sms.variables.teacherFirstName',
  last_name: 'sms.variables.teacherLastName',
};

/** ТЗ 0084: у лида и кандидата имя и фамилия — их, а не студента. */
const SMS_LEAD_VARIABLE_HINTS: Readonly<Record<string, string>> = {
  first_name: 'sms.variables.leadFirstName',
  last_name: 'sms.variables.leadLastName',
};
const SMS_CANDIDATE_VARIABLE_HINTS: Readonly<Record<string, string>> = {
  first_name: 'sms.variables.candidateFirstName',
  last_name: 'sms.variables.candidateLastName',
};

/** Свой словарь подсказок у темы, где «имя» — не студента. */
const SMS_TOPIC_VARIABLE_HINTS: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  teacher_sms: SMS_TEACHER_VARIABLE_HINTS,
  lead_sms: SMS_LEAD_VARIABLE_HINTS,
  lead_reminder: SMS_LEAD_VARIABLE_HINTS,
  lead_dofin: SMS_LEAD_VARIABLE_HINTS,
  dofin_candidate_sms: SMS_CANDIDATE_VARIABLE_HINTS,
};

/** Страница журнала. */
export const SMS_JOURNAL_PAGE_SIZE = 50;

/** Предпросмотр: печать не должна быть очередью запросов. */
export const STUDENT_SMS_PREVIEW_DEBOUNCE_MS = 400;

/** Опрос рассылки, пока она не закончилась. */
export const SMS_CAMPAIGN_POLL_MS = 4000;

/** Опрос SMS студента, пока что-то в пути. */
export const SMS_STATE_POLL_MS = 5000;

/** Предел строк сухого прогона лесенки. */
export const SMS_DRY_RUN_LIMIT = 100;

/** Имя отправителя SMSAPI — до 11 знаков. */
export const SMS_SENDER_NAME_MAX = 11;

/** Частей в SMS — предел SMSAPI. */
export const SMS_MAX_SEGMENTS_MIN = 1;
export const SMS_MAX_SEGMENTS_MAX = 10;

/** Длина текста в поле составителя (бэкенд проверяет части, не знаки). */
export const SMS_TEXT_MAX = 1000;

/** ТЗ 0084: хвост отписки — не длиннее 200 знаков (`saveSmsTexts`). */
export const SMS_OPT_OUT_SUFFIX_MAX = 200;

/**
 * Перепроверка 0084: ссылка на отписку — короткая ссылка SMS и `/stop` за
 * ней. Сервер откажет `STOP_LINK_REQUIRED`, если в хвосте её нет.
 */
export const SMS_OPT_OUT_STOP_TOKEN = '{link}/stop';

/**
 * Есть ли в хвосте ссылка на отписку. Пустой хвост — сервер возьмёт текст
 * по умолчанию: это не ошибка.
 */
export const optOutSuffixHasStop = (value: string | null | undefined) => {
  const text = String(value ?? '').trim();
  return !text || text.includes(SMS_OPT_OUT_STOP_TOKEN);
};

/** Предел длины адреса ссылки — как у сократителя (`MAX_URL`). */
export const LINK_ADDRESS_MAX = 2048;

/**
 * Перепроверка 0084: проверка адреса в «Адресах ссылок» ДО сохранения —
 * то же правило, что у сервера (`shortlinks.manual.validate_target`): без
 * схемы — `https://` добавится сам, годятся только http и https, нужен
 * хост. Пусто — адрес из настроек сервера, это не ошибка.
 */
export const linkAddressProblem = (
  raw: string | null | undefined,
): 'invalid' | 'tooLong' | null => {
  const value = String(raw ?? '').trim();
  if (!value) return null;
  const url = value.includes('://') ? value : `https://${value}`;
  if (url.length > LINK_ADDRESS_MAX) return 'tooLong';
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return 'invalid';
  }
  if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname) {
    return 'invalid';
  }
  return null;
};

type TagTone = 'ok' | 'warn' | 'danger' | 'neutral' | 'info' | 'gold';

const isOneOf = <T extends string>(
  values: readonly T[],
  value: string | null | undefined,
): value is T =>
  value !== null &&
  value !== undefined &&
  (values as readonly string[]).includes(value);

export const isSmsTopic = (
  value: string | null | undefined,
): value is SmsTopic => isOneOf(SMS_TOPICS, value);

/* ── Подписи ─────────────────────────────────────────────────────────── */

/**
 * Ключ перевода из семьи или `null` — тогда вызывающий показывает значение
 * как есть. `translate` не нужен: модель остаётся без i18next.
 */
const familyKey = (
  family: string,
  values: readonly string[],
  value: string | null | undefined,
): string | null =>
  value && values.includes(value) ? `sms.${family}.${value}` : null;

export const smsTopicKey = (topic: string | null | undefined) =>
  familyKey('topics', SMS_TOPICS, topic);

export const smsStatusKey = (status: string | null | undefined) =>
  familyKey('statuses', SMS_STATUSES, status);

export const smsSkipReasonKey = (reason: string | null | undefined) =>
  familyKey('skipReasons', SMS_SKIP_REASONS, reason);

export const smsDlrKey = (code: string | null | undefined) =>
  familyKey('dlr', SMS_DLR_FILTERS, code);

/**
 * Ошибка доставки из строки SMS (`Message.error`, пишет `outreach.deliver`):
 * известные — переводом, остальные вызывающий показывает как есть.
 * Порядок важен: «14» (имя отправителя) — частный случай блокировки канала.
 */
const DELIVERY_ERRORS: ReadonlyArray<[string, string]> = [
  ['provider_blocked: 14', 'senderInactive'],
  ['provider_blocked', 'providerBlocked'],
  ['no_funds', 'noFunds'],
  ['refresh_failed', 'refreshFailed'],
  ['interrupted', 'interrupted'],
];

export const smsDeliveryErrorKey = (
  error: string | null | undefined,
): string | null => {
  const found = DELIVERY_ERRORS.find(([prefix]) =>
    (error || '').startsWith(prefix),
  );
  return found ? `sms.deliveryErrors.${found[1]}` : null;
};

export const smsErrorKey = (code: string | null | undefined) =>
  familyKey('errors', STUDENT_SMS_ERROR_CODES, code);

export const smsSenderStatusKey = (code: string | null | undefined) =>
  familyKey('senderStatus', SMS_SENDER_STATUSES, code);

/** Подсказка чипа подстановки: `number` у фактур — номер фактуры. */
export const smsVariableHintKey = (
  topic: string,
  name: string,
): string | null => {
  if (name === 'number') {
    if (topic === SMS_ESIGN_TOPIC) return 'sms.variables.contractNumber';
    return 'sms.variables.number';
  }
  const own = SMS_TOPIC_VARIABLE_HINTS[topic]?.[name];
  if (own) return own;
  return familyKey('variables', SMS_VARIABLE_NAMES, name);
};

/* ── Тоны ────────────────────────────────────────────────────────────── */

export interface ISmsMessageLike {
  status: string;
  dlrStatus?: string | null;
  deliveredAt?: string | null;
  scheduledFor?: string | null;
  sentAt?: string | null;
  createdAt: string;
}

/**
 * Тон статуса. Отправленное, но не подтверждённое оператором — «инфо»:
 * зелёный обещал бы, что SMS дошла.
 */
export const smsStatusTone = (message: ISmsMessageLike): TagTone => {
  switch (message.status) {
    case 'sent':
      if (message.deliveredAt || message.dlrStatus === 'DELIVERED') {
        return 'ok';
      }
      return isOneOf(DLR_BAD, message.dlrStatus) ? 'danger' : 'info';
    case 'failed':
      return 'danger';
    case 'pending':
    case 'sending':
      return 'warn';
    default:
      return 'neutral';
  }
};

export const smsDlrTone = (code: string | null | undefined): TagTone => {
  if (!code) return 'neutral';
  if (code === 'DELIVERED') return 'ok';
  if (code === 'STOP') return 'warn';
  return isOneOf(DLR_BAD, code) ? 'danger' : 'info';
};

/** Время строки: когда ушла, иначе когда запланирована, иначе поставлена. */
export const smsMessageWhen = (message: ISmsMessageLike): string =>
  message.sentAt || message.scheduledFor || message.createdAt;

/**
 * Есть ли SMS, исход которой увидит только опрос: отправляется или ждёт
 * без переноса на утро (тихие часы — это часы, опрашивать нечего).
 */
export const isSmsInFlight = (
  messages: ReadonlyArray<Pick<ISmsMessageLike, 'status' | 'scheduledFor'>>,
  now: number = Date.now(),
): boolean =>
  messages.some(
    (message) =>
      message.status === 'sending' ||
      (message.status === 'pending' &&
        (!message.scheduledFor ||
          new Date(message.scheduledFor).getTime() <= now)),
  );

/* ── Лесенки ────────────────────────────────────────────────────────── */

/** Минуты шага как «N дн. M ч. K мин.» — для подсказки рядом с полем. */
export const splitOffsetMinutes = (
  minutes: number,
): { days: number; hours: number; minutes: number } => {
  const total = Math.max(0, Math.floor(Number(minutes) || 0));
  return {
    days: Math.floor(total / (24 * 60)),
    hours: Math.floor((total % (24 * 60)) / 60),
    minutes: total % 60,
  };
};

/**
 * Крупнейшая единица, в которой сдвиг записывается целым числом: 300 мин
 * — это «5 часов», а не «300 минут».
 */
export const offsetUnitOf = (minutes: number): SmsOffsetUnit => {
  const total = Math.max(0, Math.floor(Number(minutes) || 0));
  if (total && total % SMS_OFFSET_UNIT_MINUTES.days === 0) return 'days';
  if (total && total % SMS_OFFSET_UNIT_MINUTES.hours === 0) return 'hours';
  return 'minutes';
};

/**
 * Ключ подписи якоря — у каждой лесенки свой смысл: у оплаты `deadline`
 * — «до срока оплаты», `start` — «после срока оплаты» (якорь оплаты —
 * `payment_to`, и «от начала» считается от него же); у подписи `start` —
 * «после отправки договора», `deadline` — «до истечения ссылки».
 */
export const smsAnchorKey = (topic: string, anchor: string): string => {
  const plan = isOneOf(SMS_PLAN_TOPICS, topic) ? topic : 'other';
  const value = isOneOf(SMS_PLAN_ANCHORS, anchor) ? anchor : 'start';
  return `sms.plans.anchors.${plan}.${value}`;
};

/**
 * ТЗ 0082: подпись состояния повода. Состояния у тем разные («не открыл»
 * у договора, «просрочена» у фактуры), поэтому ключ — с темой.
 */
export const smsConditionKey = (topic: string, condition: string): string => {
  const plan = isOneOf(SMS_PLAN_TOPICS, topic) ? topic : 'other';
  return `sms.plans.conditions.${plan}.${condition}`;
};

export interface ISmsPlanStepDraft {
  /** Ключ строки в форме: id шага или временный для нового. */
  key: string;
  step: number;
  anchor: SmsPlanAnchor;
  /** Сырой ввод: полупустое поле не превращается молча в ноль. */
  offsetValue: string;
  offsetUnit: SmsOffsetUnit;
  /** Состояние повода; пусто — «в любом». */
  condition: string;
  templateCode: string;
  enabled: boolean;
}

export interface ISmsPlanStepInput {
  step: number;
  anchor: string;
  offsetMinutes: number;
  condition: string;
  templateCode: string;
  enabled: boolean;
}

/** Целое ≥ 0 или `null`. */
export const parseOffsetValue = (raw: string): number | null => {
  const text = String(raw ?? '').trim();
  if (!/^\d+$/.test(text)) return null;
  return Number(text);
};

/** Сдвиг строки формы в минутах (`null` — введено не число). */
export const draftOffsetMinutes = (
  step: Pick<ISmsPlanStepDraft, 'offsetValue' | 'offsetUnit'>,
): number | null => {
  const value = parseOffsetValue(step.offsetValue);
  if (value === null) return null;
  return value * (SMS_OFFSET_UNIT_MINUTES[step.offsetUnit] ?? 1);
};

export type SmsPlanStepProblem =
  | 'offset'
  | 'template'
  | 'duplicate'
  | 'stepRange'
  | 'conditionOrder'
  | 'impossible';

/** Словарь темы для проверки шагов (`ReminderPlanType`). */
export interface ISmsPlanRules {
  /** Состояния в порядке прохождения. */
  states?: ReadonlyArray<string>;
  /** «якорь:условие», которые не сойдутся никогда. */
  impossibleSteps?: ReadonlyArray<string>;
}

/**
 * Ошибки строк формы лесенки — те же правила, что у сервера.
 *
 * 🔴 `conditionOrder`: номера шагов обязаны идти в порядке состояний
 * (`states` темы). Робот не отправляет шаг с номером меньше уже
 * сделанного, поэтому шаг позднего состояния, стоящий выше по номеру,
 * молча потерялся бы.
 */
export const validatePlanSteps = (
  steps: ReadonlyArray<ISmsPlanStepDraft>,
  rules: ISmsPlanRules = {},
): Record<string, SmsPlanStepProblem[]> => {
  const states = rules.states ?? [];
  const impossible = new Set(rules.impossibleSteps ?? []);
  const out: Record<string, SmsPlanStepProblem[]> = {};
  const seen = new Map<number, number>();
  steps.forEach((step) => seen.set(step.step, (seen.get(step.step) ?? 0) + 1));
  const place = (condition: string) => states.indexOf(condition);
  const conditioned = steps
    .filter((step) => step.condition && place(step.condition) >= 0)
    .sort((a, b) => a.step - b.step);
  const outOfOrder = new Set<string>();
  conditioned.forEach((step, index) => {
    if (index === 0) return;
    const previous = conditioned[index - 1];
    if (place(step.condition) < place(previous.condition)) {
      outOfOrder.add(step.key);
    }
  });
  steps.forEach((step) => {
    const problems: SmsPlanStepProblem[] = [];
    if (!Number.isInteger(step.step) || step.step < 0 || step.step > 99) {
      problems.push('stepRange');
    }
    if ((seen.get(step.step) ?? 0) > 1) problems.push('duplicate');
    if (draftOffsetMinutes(step) === null) problems.push('offset');
    if (!step.templateCode) problems.push('template');
    if (outOfOrder.has(step.key)) problems.push('conditionOrder');
    if (step.condition && impossible.has(`${step.anchor}:${step.condition}`)) {
      problems.push('impossible');
    }
    if (problems.length) out[step.key] = problems;
  });
  return out;
};

/** Вход мутации из формы; `null` — в форме есть ошибки. */
export const planStepsInput = (
  steps: ReadonlyArray<ISmsPlanStepDraft>,
  rules: ISmsPlanRules = {},
): ISmsPlanStepInput[] | null => {
  if (Object.keys(validatePlanSteps(steps, rules)).length) return null;
  return steps.map((step) => ({
    step: step.step,
    anchor: step.anchor,
    offsetMinutes: draftOffsetMinutes(step) ?? 0,
    condition: step.condition,
    templateCode: step.templateCode,
    enabled: step.enabled,
  }));
};

/**
 * Номер нового шага — следующий за последним: «Добавить шаг» ставит шаг в
 * конец лесенки, как и выглядит на экране. Первый свободный номер
 * посередине (после удаления) встал бы между ветками состояний и сломал
 * бы порядок. Конец занят (99) — первый свободный.
 */
export const nextPlanStepNumber = (
  steps: ReadonlyArray<Pick<ISmsPlanStepDraft, 'step'>>,
): number => {
  const used = new Set(steps.map((step) => step.step));
  const last = steps.reduce((max, step) => Math.max(max, step.step), -1);
  if (last < 99) return last + 1;
  for (let n = 0; n < 100; n += 1) {
    if (!used.has(n)) return n;
  }
  return 99;
};

/* ── Ссылка в SMS ────────────────────────────────────────────────────── */

export interface ISmsLinkDraft {
  kind: SmsLinkKind;
  invoiceId: string;
  url: string;
  /** Документ на подпись преподавателя (вид `sign`). */
  contractId?: string;
}

export const EMPTY_SMS_LINK: ISmsLinkDraft = {
  kind: 'none',
  invoiceId: '',
  url: '',
  contractId: '',
};

/**
 * Вход ссылки: `SmsLinkInput` (студенту, группе) или `TeacherSmsLinkInput`
 * (преподавателю). Поля у каждого вида только свои — лишнее поле сервер
 * отверг бы как неизвестное.
 */
export interface ISmsLinkInput {
  kind: string;
  invoiceId?: string | null;
  contractId?: string | null;
  url?: string | null;
}

/**
 * Вход ссылки из выбора в форме. «Без ссылки» уходит `null`: сервер
 * считает это тем же, а предпросмотр и отправка получают одну запись кэша.
 */
export const smsLinkInput = (link: ISmsLinkDraft): ISmsLinkInput | null => {
  switch (link.kind) {
    case 'invoice':
      return { kind: 'invoice', invoiceId: link.invoiceId || null };
    case 'sign':
      return { kind: 'sign', contractId: link.contractId || null };
    case 'url':
      return { kind: 'url', url: link.url.trim() };
    case 'lms':
    case 'lms_invoices':
    case 'dofin_form':
      return { kind: link.kind };
    default:
      return null;
  }
};

/**
 * Ссылка выбрана не до конца (адрес пуст, документ на подпись не выбран) —
 * отправлять рано.
 */
export const smsLinkIncomplete = (link: ISmsLinkDraft): boolean =>
  (link.kind === 'url' && !link.url.trim()) ||
  (link.kind === 'sign' && !link.contractId);

/* ── Адрес прямо в тексте (перепроверка 0084) ─────────────────────────── */

/**
 * Знаки, на которых адрес кончается: пробел, угловые скобки, кавычки (в том
 * числе польские „…” и «…»), многоточие одним знаком — как `_URL_STOP`
 * сервера (`outreach/services/links.py`).
 */
const URL_STOP = '\\s<>"\'\u201e\u201c\u201d\u00ab\u00bb\u2026';

/**
 * Адрес — `http(s)://…` или `www.<домен>.…` до знака из `URL_STOP`. Своя
 * регулярка на вызов (у глобальной `lastIndex` общий). Проверку «перед
 * `www.` не буква, не `@`, не точка» делает код, а не `(?<!…)`: ретроспективу
 * старый Safari не разбирает вовсе — упала бы вся сборка.
 */
const textUrlPattern = () =>
  new RegExp(
    `(?:https?://|www\\.(?=[\\p{L}\\p{N}_-]+\\.[\\p{L}\\p{N}_-]))[^${URL_STOP}]+`,
    'giu',
  );

/** Перед `www.` — часть e-mail или другого слова: это не адрес. */
const WWW_GLUED_RE = /[\p{L}\p{N}_@./-]/u;

/** Знаки, которыми кончается фраза, а не адрес: «…na stronie https://x.pl.» */
const URL_TAIL = '.,;:!?';

/** Закрывающая скобка в хвосте — не часть адреса, если в нём нет её пары. */
const URL_CLOSERS: Record<string, string> = { ')': '(', ']': '[' };

const countOf = (text: string, char: string): number =>
  text.split(char).length - 1;

/**
 * Хвост адреса — не адрес: знаки конца предложения и непарная закрывающая
 * скобка (`split_tail` сервера): «(https://x.pl/a)» → `https://x.pl/a`, а
 * `https://pl.wikipedia.org/wiki/Python_(język)` остаётся целым.
 */
const splitUrlTail = (raw: string): string => {
  let url = raw;
  while (url) {
    const last = url[url.length - 1];
    const pair = URL_CLOSERS[last];
    if (
      URL_TAIL.includes(last) ||
      (pair !== undefined && countOf(url, last) > countOf(url, pair))
    ) {
      url = url.slice(0, -1);
    } else {
      break;
    }
  }
  return url;
};

/** Адрес в тексте: как он написан (`raw`) и куда поведёт ссылка (`target`). */
export interface ITextUrl {
  raw: string;
  /** `www.…` без схемы — `https://www.…` (`as_target` сервера). */
  target: string;
}

/**
 * Адреса, вставленные прямо в текст SMS, — то же правило, что у сервера
 * (`URL_RE`, `split_tail`, `as_target`): `http(s)://…` и `www.…`, хвостовая
 * пунктуация и непарная скобка — не часть адреса. Сервер сам заменит
 * каждый такой адрес короткой ссылкой при отправке (переходы посчитаются);
 * наш короткий адрес тоже находится — экран предупреждает о нём.
 */
export const findTextUrlMatches = (text: string): ITextUrl[] => {
  const source = String(text ?? '');
  const pattern = textUrlPattern();
  const found: ITextUrl[] = [];
  let match = pattern.exec(source);
  while (match !== null) {
    const glued =
      match[0].slice(0, 4).toLowerCase() === 'www.' &&
      match.index > 0 &&
      WWW_GLUED_RE.test(source[match.index - 1]);
    const raw = glued ? '' : splitUrlTail(match[0]);
    const target =
      raw.slice(0, 4).toLowerCase() === 'www.' ? `https://${raw}` : raw;
    if (
      raw &&
      /^https?:\/\/[^/\s]+/i.test(target) &&
      !found.some((item) => item.target === target)
    ) {
      found.push({ raw, target });
    }
    match = pattern.exec(source);
  }
  return found;
};

/** Цели адресов текста — по порядку, без повторов. */
export const findTextUrls = (text: string): string[] =>
  findTextUrlMatches(text).map((item) => item.target);

/**
 * «Перенести в поле «Ссылка»»: адрес (как он написан в тексте — `raw`)
 * уходит из текста, на его место встаёт `{link}` — туда сервер подставит
 * короткую ссылку (адрес переезжает в поле «Ссылка → Любой адрес»).
 * Меняется только первое вхождение.
 */
export const moveUrlToLink = (text: string, url: string): string => {
  const at = text.indexOf(url);
  if (at < 0) return text;
  return `${text.slice(0, at)}{link}${text.slice(at + url.length)}`;
};

/**
 * Домены наших коротких ссылок. Боевой — `s.learnit.com.pl` (адрес вида
 * `https://s.learnit.com.pl/Ab12Cd34`); стенд настраивает свой через
 * `REACT_APP_SHORTLINK_BASE_URL`. Локально сократитель отдаёт
 * `http://…/s/<код>` — это ловит путь `/s/<код>`.
 */
export const OWN_SHORT_LINK_HOSTS: readonly string[] = ['s.learnit.com.pl'];

const hostOf = (url: string | undefined): string => {
  try {
    return url ? new URL(url).host.toLowerCase() : '';
  } catch {
    return '';
  }
};

/** Хосты, на которых живут НАШИ короткие ссылки: боевой домен, настроенный
 * сократитель и сам бэкенд (`<бэкенд>/s/<код>` — локально и на стенде). */
const ownShortHosts = (): string[] =>
  [
    ...OWN_SHORT_LINK_HOSTS,
    hostOf(process.env.REACT_APP_SHORTLINK_BASE_URL),
    hostOf(process.env.REACT_APP_GRAPH_URI),
  ].filter(Boolean);

/**
 * ТЗ 0084: образец короткой ссылки той же длины, что выдаст сократитель
 * (`message_templates.sample_link` бэкенда: база + `/` + 8 знаков кода).
 * Сократитель не настроен — боевой домен.
 */
export const SMS_SAMPLE_LINK_CODE = 'XXXXXXXX';
export const smsSampleShortLink = (base: string | null | undefined): string =>
  `${(base || 'https://s.learnit.com.pl').replace(
    /\/+$/,
    '',
  )}/${SMS_SAMPLE_LINK_CODE}`;

/** Пробелы схлопываются, как на сервере (`opt_out_suffix`). */
export const normalizeOptOutSuffix = (value: string | null | undefined) =>
  String(value ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');

/**
 * Сколько знаков хвост отписки добавит к первой SMS рассылки: пробел перед
 * ним + текст с `{link}`, посчитанным короткой ссылкой. Пустой хвост —
 * сервер возьмёт текст по умолчанию (`fallback`).
 */
export const optOutSuffixLength = (
  suffix: string | null | undefined,
  shortlinkBase: string | null | undefined,
  fallback = '',
): number => {
  const value =
    normalizeOptOutSuffix(suffix) || normalizeOptOutSuffix(fallback);
  if (!value) return 0;
  const link = smsSampleShortLink(shortlinkBase);
  return 1 + value.split('{link}').join(link).length;
};

/**
 * Это уже НАША короткая ссылка? Её не сокращают второй раз: переходы
 * разделились бы между двумя ссылками — предупреждаем, но не запрещаем.
 */
export const isOwnShortLink = (raw: string | null | undefined): boolean => {
  const value = String(raw ?? '').trim();
  if (!/^https?:\/\//i.test(value)) return false;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  const host = parsed.host.toLowerCase();
  const path = parsed.pathname.replace(/\/+$/, '');
  // Только наши хосты: у чужого сайта путь `/s/…` — обычная страница
  // (ревью 0084: `indeed.pl/s/oferta12` терял «Сделать короткой»).
  if (!ownShortHosts().includes(host)) return false;
  return (
    /^\/[A-Za-z0-9_-]{4,}$/.test(path) || /^\/s\/[A-Za-z0-9_-]{6,}$/.test(path)
  );
};

/* ── Повтор с тем же ключом вернул прежнюю SMS (перепроверка 0084) ───── */

/** Сравнение без регистра, лишних пробелов и польских «хвостиков». */
const foldSms = (value: string): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[łŁ]/g, 'l')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const escapeRe = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Написан ли `body` (текст SMS с сервера) по этому тексту формы. Подстановки
 * (`{first_name}`, `{link}`) — любой кусок, ссылка может встать в конец:
 * сравнивается начало. Нужен одному случаю — повтору с тем же ключом после
 * сбоя сети, когда текст успели поправить: сервер вернёт ПРЕЖНЮЮ SMS, а не
 * отправит новую.
 */
export const smsBodyMatchesText = (body: string, text: string): boolean => {
  const folded = foldSms(text);
  if (!folded) return false;
  const pattern = folded
    .split(/\{[a-z_]+\}/)
    .map((part) => escapeRe(part))
    .join('[\\s\\S]*?');
  return new RegExp(`^${pattern}`).test(foldSms(body));
};

/* ── Выбор получателей рассылки ─────────────────────────────────────── */

/**
 * Кому из кандидатов рассылки SMS может уйти: без причины исключения. Только
 * их можно отметить — у исключённого флажка нет.
 */
export const sendableCandidateIds = (
  candidates: ReadonlyArray<{ studentId: string; reason?: string | null }>,
): string[] =>
  candidates
    .filter((candidate) => !candidate.reason)
    .map((candidate) => candidate.studentId);

/** Состояние флажка «выбрать всех»: все / часть (неопределённое) / никто. */
export const selectionState = (
  selected: ReadonlySet<string>,
  offered: readonly string[],
): 'all' | 'some' | 'none' => {
  const picked = offered.filter((id) => selected.has(id)).length;
  if (picked === 0) return 'none';
  return picked === offered.length ? 'all' : 'some';
};

/**
 * Причины, которые касаются КАНАЛА, а не человека: если получатель исключён
 * по такой, дело не в нём — SMS сейчас не уйдёт никому.
 */
const CHANNEL_LEVEL_REASONS = [
  'disabled_on_server',
  'disabled',
  'entity_not_allowed',
];

export interface ISmsAvailability {
  /** `null` — состояние ещё не известно: плашку рисовать рано. */
  available: boolean | null;
  /** Машинная причина «нельзя» или `null`. */
  reason: string | null;
}

/**
 * Можно ли рассылать по группе — тем же языком, что у панелей студента и
 * преподавателя. Пока кандидаты не пришли — `null` (плашки нет). Некому
 * отправить: если у исключённых причина канальная (SMS выключены, юрлицо не
 * включено) — она и есть ответ; если у всех одна и та же — она; иначе
 * причины разные, и вызывающий говорит «все исключены» своим текстом.
 */
export const groupSmsAvailability = (
  candidates: ReadonlyArray<{ studentId: string; reason?: string | null }>,
  settled: boolean,
): ISmsAvailability => {
  if (!settled) return { available: null, reason: null };
  if (candidates.length === 0) return { available: null, reason: null };
  if (sendableCandidateIds(candidates).length > 0) {
    return { available: true, reason: null };
  }
  const reasons = candidates.map((candidate) => candidate.reason || '');
  const channel = reasons.find((reason) =>
    CHANNEL_LEVEL_REASONS.includes(reason),
  );
  if (channel) return { available: false, reason: channel };
  const [first] = reasons;
  const same = reasons.every((reason) => reason === first);
  return { available: false, reason: same ? first : null };
};

/* ── Почему «Отправить» не нажимается ───────────────────────────────── */

/**
 * Одна причина, по которой отправка сейчас запрещена. Кнопка и подпись под
 * ней считаются ОДНОЙ функцией: «серая кнопка без объяснения» — тупик, а
 * два независимых условия однажды разойдутся (грабли
 * `counter-must-match-the-button`).
 */
export const SMS_SEND_BLOCKS = [
  'empty',
  'nobody',
  'unknownPlaceholder',
  'linkIncomplete',
  'previewing',
  'tooLong',
  'unavailable',
  // ТЗ 0083, массовая SMS по договорам: проверка не удалась; выбрано больше
  // предела; выбранным договорам SMS не уйдёт никому.
  'previewFailed',
  'tooMany',
  'noneEligible',
  // Договор подписан, а в тексте `{link}`: ссылки на подпись больше нет.
  'linkAfterSigning',
] as const;
export type SmsSendBlock = typeof SMS_SEND_BLOCKS[number];

export interface ISmsSendCheck {
  text: string;
  /** Предпросмотр отстал от текста или ещё едет. */
  pending: boolean;
  /** Сколько подстановок текста сервер не знает. */
  unknownCount: number;
  /** Ссылка выбрана не до конца (пустой адрес, не выбран документ). */
  linkIncomplete: boolean;
  /** Ответ предпросмотра уже есть. */
  previewReady: boolean;
  /** Предпросмотр разрешает отправку. */
  available: boolean;
  tooLong: boolean;
  /** Рассылка: сколько получателей выбрано. `null` — адресат один. */
  recipients?: number | null;
}

export const smsSendBlock = (check: ISmsSendCheck): SmsSendBlock | null => {
  if (!check.text.trim()) return 'empty';
  if (check.recipients !== null && check.recipients !== undefined) {
    if (check.recipients <= 0) return 'nobody';
  }
  if (check.unknownCount > 0) return 'unknownPlaceholder';
  if (check.linkIncomplete) return 'linkIncomplete';
  if (check.pending || !check.previewReady) return 'previewing';
  if (check.tooLong) return 'tooLong';
  if (!check.available) return 'unavailable';
  return null;
};

/** Ключ перевода причины запрета отправки. */
export const smsSendBlockKey = (block: SmsSendBlock): string =>
  `sms.send.blocked.${block}`;

/* ── Ключ запроса (защита от двойной отправки) ──────────────────────── */

/**
 * Ключ одной отправки: создаётся, когда форма открылась (или после удачной
 * отправки), и тот же уходит при повторе того же нажатия — сервер отдаст
 * уже поставленную SMS, а не вторую.
 */
export const newSmsRequestKey = (): string => `sms-${nanoid(24)}`;

/* ── Фильтры журнала в адресе ───────────────────────────────────────── */

export const SMS_JOURNAL_FILTER_PARAMS = {
  topic: 'topic',
  status: 'status',
  dlr: 'dlr',
  student: 'student',
  group: 'group',
  teacher: 'teacher',
  contract: 'contract',
  lead: 'lead',
  candidate: 'candidate',
  campaign: 'campaign',
  author: 'author',
  from: 'from',
  to: 'to',
  phone: 'phone',
  search: 'q',
} as const;

export interface ISmsJournalFilters {
  topic: string;
  status: string;
  dlr: string;
  student: string;
  group: string;
  /** Преподаватель-получатель (тема `teacher_sms`). */
  teacher: string;
  /** Договор: всё, что уходило по его подписи (ссылка из карточки договора). */
  contract: string;
  /** ТЗ 0084: лид Kommo — SMS ему (ссылка из карточки лида). */
  lead: string;
  /** ТЗ 0084: кандидат дофинансирования — SMS ему. */
  candidate: string;
  campaign: string;
  author: string;
  /** `YYYY-MM-DD`, сутки по Варшаве, включительно. */
  from: string;
  to: string;
  phone: string;
  search: string;
}

export const EMPTY_SMS_JOURNAL_FILTERS: ISmsJournalFilters = {
  topic: '',
  status: '',
  dlr: '',
  student: '',
  group: '',
  teacher: '',
  contract: '',
  lead: '',
  candidate: '',
  campaign: '',
  author: '',
  from: '',
  to: '',
  phone: '',
  search: '',
};

const cleanDay = (value: string | null): string => {
  const day = (value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(day) &&
    moment(day, 'YYYY-MM-DD', true).isValid()
    ? day
    : '';
};

/** Идентификатор из адреса: только цифры или base64 id Relay. */
const cleanId = (value: string | null): string => {
  const id = (value || '').trim();
  return /^[A-Za-z0-9+/=_-]{1,120}$/.test(id) ? id : '';
};

const cleanDigits = (value: string | null): string =>
  (value || '').replace(/\D+/g, '').slice(0, 15);

/**
 * Фильтры из строки адреса. Незнакомое значение словаря — пусто, а не на
 * сервер: там оно молча не фильтровало бы ничего, а чип всё равно не горел
 * бы — список и фильтры разошлись бы.
 */
export const parseSmsJournalFilters = (search: string): ISmsJournalFilters => {
  const params = new URLSearchParams(search);
  const get = (key: keyof typeof SMS_JOURNAL_FILTER_PARAMS) =>
    params.get(SMS_JOURNAL_FILTER_PARAMS[key]);
  const topic = get('topic');
  const status = get('status');
  const dlr = get('dlr');
  return {
    topic: isOneOf(SMS_TOPICS, topic) ? topic : '',
    status: isOneOf(SMS_STATUSES, status) ? status : '',
    dlr: isOneOf(SMS_DLR_FILTERS, dlr) ? dlr : '',
    student: cleanId(get('student')),
    group: cleanId(get('group')),
    teacher: cleanId(get('teacher')),
    contract: cleanId(get('contract')),
    lead: cleanId(get('lead')),
    candidate: cleanId(get('candidate')),
    campaign: cleanId(get('campaign')),
    author: cleanId(get('author')),
    from: cleanDay(get('from')),
    to: cleanDay(get('to')),
    phone: cleanDigits(get('phone')),
    search: (get('search') || '').trim(),
  };
};

/**
 * Новая строка адреса: `patch` поверх фильтров. Прочие параметры (`?tab=`)
 * не трогаются; пустое значение удаляет параметр.
 */
export const serializeSmsJournalFilters = (
  search: string,
  patch: Partial<ISmsJournalFilters>,
): string => {
  const params = new URLSearchParams(search);
  const next = { ...parseSmsJournalFilters(search), ...patch };
  (Object.keys(SMS_JOURNAL_FILTER_PARAMS) as Array<
    keyof ISmsJournalFilters
  >).forEach((key) => {
    const param = SMS_JOURNAL_FILTER_PARAMS[key];
    const value = String(next[key] ?? '').trim();
    if (value) params.set(param, value);
    else params.delete(param);
  });
  return params.toString();
};

export const hasSmsJournalFilters = (filters: ISmsJournalFilters): boolean =>
  (Object.keys(filters) as Array<keyof ISmsJournalFilters>).some((key) =>
    Boolean(filters[key]),
  );

/** Переменные запроса журнала: пустое — `null` (одна запись кэша). */
export const toSmsJournalVariables = (filters: ISmsJournalFilters) => ({
  topic: filters.topic || null,
  status: filters.status || null,
  dlrStatus: filters.dlr || null,
  studentId: filters.student || null,
  groupId: filters.group || null,
  teacherId: filters.teacher || null,
  contractId: filters.contract || null,
  campaignId: filters.campaign || null,
  createdById: filters.author || null,
  createdFrom: filters.from || null,
  createdTo: filters.to || null,
  phoneTail: filters.phone || null,
  search: filters.search || null,
  leadId: filters.lead || null,
  candidateId: filters.candidate || null,
});

/** Вкладка страницы из адреса; незнакомая — журнал. */
export const parseSmsPageTab = (search: string): SmsPageTab => {
  const value = new URLSearchParams(search).get('tab');
  return isOneOf(SMS_PAGE_TABS, value) ? value : 'journal';
};

/** Адрес с вкладкой; журнал (по умолчанию) параметр удаляет. */
export const withSmsPageTab = (search: string, tab: SmsPageTab): string => {
  const params = new URLSearchParams(search);
  if (tab === 'journal') params.delete('tab');
  else params.set('tab', tab);
  return params.toString();
};

/**
 * Числовой id из id Relay (`U3R1ZGVudFR5cGU6MTI=` → `12`). Журнал отдаёт
 * `studentId` / `teacherId` числом, а поиск студента и карточки — бывает, что
 * id Relay: сверять их можно только так. Не id Relay — строка как есть.
 */
export const relayToPk = (id: string | null | undefined): string => {
  const value = String(id ?? '').trim();
  if (!value || /^\d+$/.test(value)) return value;
  try {
    const decoded = window.atob(value);
    const pk = decoded.split(':')[1];
    return pk && /^\d+$/.test(pk) ? pk : value;
  } catch {
    return value;
  }
};

/* ── Настройки ──────────────────────────────────────────────────────── */

/** `HH:MM` — как принимает сервер. */
export const isSmsTime = (value: string): boolean =>
  /^([01]\d|2[0-3]):[0-5]\d$/.test(value.trim());

/** `HH:MM:SS` → `HH:MM`: `input type=time` не любит секунды. */
export const toSmsTime = (value: string | null | undefined): string => {
  const match = /^(\d{2}):(\d{2})/.exec(String(value ?? ''));
  return match ? `${match[1]}:${match[2]}` : '';
};

export interface ISmsSettingsLike {
  enabled: boolean;
  remindersEnabled: boolean;
  senderName: string;
  quietStart: string;
  quietEnd: string;
  dailyCapPerPhone: number;
  allowUnicode: boolean;
  allowForeignNumbers: boolean;
  maxSegments: number;
  lowBalancePoints?: number | null;
}

export interface ISmsSettingsDraft {
  enabled: boolean;
  remindersEnabled: boolean;
  senderName: string;
  quietStart: string;
  quietEnd: string;
  dailyCapPerPhone: string;
  allowUnicode: boolean;
  allowForeignNumbers: boolean;
  maxSegments: string;
  lowBalancePoints: string;
}

export const smsSettingsDraft = (
  settings: ISmsSettingsLike,
): ISmsSettingsDraft => ({
  enabled: settings.enabled,
  remindersEnabled: settings.remindersEnabled,
  senderName: settings.senderName,
  quietStart: toSmsTime(settings.quietStart),
  quietEnd: toSmsTime(settings.quietEnd),
  dailyCapPerPhone: String(settings.dailyCapPerPhone),
  allowUnicode: settings.allowUnicode,
  allowForeignNumbers: settings.allowForeignNumbers,
  maxSegments: String(settings.maxSegments),
  lowBalancePoints:
    settings.lowBalancePoints === null ||
    settings.lowBalancePoints === undefined
      ? ''
      : String(settings.lowBalancePoints),
});

export type SmsSettingsField = keyof ISmsSettingsDraft;

/** Ошибки формы настроек — те же правила, что у сервера. */
export const validateSmsSettings = (
  draft: ISmsSettingsDraft,
): Partial<Record<SmsSettingsField, string>> => {
  const out: Partial<Record<SmsSettingsField, string>> = {};
  const name = draft.senderName.trim();
  if (!name || name.length > SMS_SENDER_NAME_MAX) {
    out.senderName = 'sms.settings.errors.senderName';
  }
  if (!isSmsTime(draft.quietStart)) {
    out.quietStart = 'sms.settings.errors.time';
  }
  if (!isSmsTime(draft.quietEnd)) out.quietEnd = 'sms.settings.errors.time';
  if (!/^\d+$/.test(draft.dailyCapPerPhone.trim())) {
    out.dailyCapPerPhone = 'sms.settings.errors.cap';
  }
  const parts = Number(draft.maxSegments.trim());
  if (
    !/^\d+$/.test(draft.maxSegments.trim()) ||
    parts < SMS_MAX_SEGMENTS_MIN ||
    parts > SMS_MAX_SEGMENTS_MAX
  ) {
    out.maxSegments = 'sms.settings.errors.segments';
  }
  const low = draft.lowBalancePoints.trim().replace(',', '.');
  if (low && !(Number.isFinite(Number(low)) && Number(low) >= 0)) {
    out.lowBalancePoints = 'sms.settings.errors.lowBalance';
  }
  return out;
};

export interface ISmsSettingsInput {
  enabled?: boolean;
  remindersEnabled?: boolean;
  senderName?: string;
  quietStart?: string;
  quietEnd?: string;
  dailyCapPerPhone?: number;
  allowUnicode?: boolean;
  allowForeignNumbers?: boolean;
  maxSegments?: number;
  lowBalancePoints?: number;
  clearLowBalance?: boolean;
}

/** Только изменённые поля — частичное сохранение, как ждёт сервер. */
export const smsSettingsInput = (
  saved: ISmsSettingsLike,
  draft: ISmsSettingsDraft,
): ISmsSettingsInput => {
  const base = smsSettingsDraft(saved);
  const input: ISmsSettingsInput = {};
  ([
    'enabled',
    'remindersEnabled',
    'allowUnicode',
    'allowForeignNumbers',
  ] as const).forEach((key) => {
    if (draft[key] !== base[key]) input[key] = draft[key];
  });
  if (draft.senderName.trim() !== base.senderName) {
    input.senderName = draft.senderName.trim();
  }
  if (draft.quietStart !== base.quietStart) input.quietStart = draft.quietStart;
  if (draft.quietEnd !== base.quietEnd) input.quietEnd = draft.quietEnd;
  if (draft.dailyCapPerPhone.trim() !== base.dailyCapPerPhone) {
    input.dailyCapPerPhone = Number(draft.dailyCapPerPhone.trim());
  }
  if (draft.maxSegments.trim() !== base.maxSegments) {
    input.maxSegments = Number(draft.maxSegments.trim());
  }
  const low = draft.lowBalancePoints.trim().replace(',', '.');
  if (low !== base.lowBalancePoints) {
    if (low) input.lowBalancePoints = Number(low);
    else input.clearLowBalance = true;
  }
  return input;
};
